import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getJobs } from "../services/jobsService";
import { isSupabaseConfigured } from "../supabase/client";
import "./Careers.css";

const Careers = () => {
  // Live job openings (admin-managed). Empty until Supabase is configured.
  const [openings, setOpenings] = useState([]);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    getJobs()
      .then((jobs) => setOpenings(jobs.filter((j) => j.active !== false)))
      .catch(() => {/* no openings on error */});
  }, []);
  // ─── Elevated Benefits Data ───
  const benefits = [
    {
      title: "Genuine Well-Being",
      desc: "Competitive pay and genuine attention to physical, mental, and financial health. Salaries are reviewed on a fixed annual cycle, so pay keeps pace with responsibility."
    },
    {
      title: "Structured Growth",
      desc: "Deliberate skill development, from technical programs on the floor through to AOTS management training in Japan. Staff who train abroad are expected to bring that knowledge back to their teams."
    },
    {
      title: "A Culture That Listens",
      desc: "A floor where ideas travel up as quickly as instructions travel down. Proposals to improve an operation are tested, not filed."
    },
    {
      title: "Work That Matters",
      desc: "Help make one of Pakistan's flagship export industries cleaner, more efficient, and more competitive. The work is commercial, but its effect reaches well beyond the company."
    }
  ];

  // ─── Animation Variants ───
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  return (
    <div className="careers-modern-page">

      {/* ── Compact Banner Hero ── */}
      <section
        className="banner-hero"
        style={{ backgroundImage: `url('/img/Careers/pattern-making.jpeg')` }}
      >
        <div className="banner-overlay"></div>
        <div className="container banner-content">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="copper-eyebrow">Careers at Softwood</span>
            <h1 className="hero-title-bold">
              Your Work, Worn <span className="hero-italic">Worldwide.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── Live Openings (admin-managed) ── */}
      {openings.length > 0 && (
        <section className="openings-section">
          <div className="container">
            <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2>Open Positions</h2>
              <p className="subtitle">{openings.length} role{openings.length === 1 ? "" : "s"} currently available.</p>
              <div className="copper-divider center-div"></div>
            </motion.div>

            <motion.div className="openings-list" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
              {openings.map((job) => (
                <motion.div className="opening-card" key={job.id} variants={fadeUp}>
                  <div className="opening-main">
                    <h3>{job.title}</h3>
                    <div className="opening-meta">
                      {[job.department, job.location, job.type].filter(Boolean).map((m, i) => (
                        <span className="opening-tag" key={i}>{m}</span>
                      ))}
                    </div>
                    {job.description && <p className="opening-desc">{job.description}</p>}
                  </div>
                  <Link
                    className="opening-apply"
                    to={`/careers/apply/${job.id}`}
                    state={{ job }}
                  >
                    Apply →
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ── High-Contrast Overview Split ── */}
      <section className="culture-overview-section">
        <div className="container split-layout">
          <motion.div className="overview-text" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>See Your Impact Leave the Floor.</h2>
            <div className="copper-divider"></div>
            <p className="lead-text">
              A merchandiser's decision in Lahore reaches a rack in Paris. Join a 500-strong team whose work ships to brands in more than 20 countries, where what you decide in the morning can be in a container by the end of the month.
            </p>
            <p>
              We invest in our people the way we invest in our machines — deliberately. Growth here is planned rather than accidental: each role carries a defined skill path, a named mentor, and a clear next step.
            </p>

            {/* New Culture Stats Grid */}
            <div className="culture-stats-grid">
              <div className="culture-stat">
                <span className="stat-num">2008</span>
                <span className="stat-label">Founded in Lahore</span>
              </div>
              <div className="culture-stat">
                <span className="stat-num">20+</span>
                <span className="stat-label">Export Markets</span>
              </div>
              <div className="culture-stat">
                <span className="stat-num">500+</span>
                <span className="stat-label">Team Members</span>
              </div>
            </div>
          </motion.div>

          <motion.div className="overview-image-wrapper" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="image-frame">
              <img src="/img/Careers/pattern-making.jpeg" alt="Pattern Making Process" className="shadow-image" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Premium Benefits Grid ── */}
      <section className="benefits-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Why Softwood?</h2>
            <p className="subtitle">What you receive in return for your best work — the terms that determine whether a role is worth building a career in.</p>
            <div className="copper-divider center-div"></div>
          </motion.div>

          <motion.div
            className="benefits-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {benefits.map((benefit, index) => (
              <motion.div className="benefit-card" key={index} variants={fadeUp}>
                <span className="benefit-num">0{index + 1}</span>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Direct Application Block ── */}
      <section className="application-section">
        <div className="container">
          <motion.div className="app-box" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="app-text-col">
              <h2>No Suitable Opening Listed?</h2>
              <p>
                We are always interested in exceptional talent. Send us your resume and we will be in touch when a matching role opens. Applications are kept on file and reviewed as vacancies arise, so it is worth writing to us even when nothing is posted.
              </p>
            </div>
            <div className="app-action-col">
              <span className="action-label">Submit Your Resume</span>
              <a href="mailto:careers@softwoodtextiles.com" className="email-link">
                careers@softwoodtextiles.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Careers;